import React, { useState, useRef, useEffect } from 'react';
import { useClients, PIPELINE_STAGES } from '../context/ClientsContext';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import { fmtK } from '../utils/formatters';
import SaveEmployerDialog from './SaveEmployerDialog';

const TEMP_KEY = 'healthcues_unsaved';

// Version stamp for cache busting
console.log('EmployerSelector v2.0 loaded - Using ClientsContext');

export default function EmployerSelector() {
  const { 
    currentClient, 
    clients, 
    hasUnsavedChanges, 
    createNewClient,
    clearUnsaved,
    loadClient,
  } = useClients();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const getStageIcon = (stage) => {
    const stageInfo = PIPELINE_STAGES[stage] || PIPELINE_STAGES.lead;
    return <Icon name={stageInfo.icon} style={{ fontSize: 16, color: stageInfo.color }} />;
  };
  
  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  const filteredClients = clients.filter(client =>
    (client.company.legalName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const recentClients = [...clients]
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 5);
  
  const handleNewClient = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Start new client anyway?')) {
        createNewClient();
        setIsOpen(false);
        setShowSaveDialog(true);
      }
    } else {
      createNewClient();
      setIsOpen(false);
      setShowSaveDialog(true);
    }
  };
  
  const handleLoadClient = (id) => {
    if (hasUnsavedChanges && currentClient?.id !== id) {
      if (window.confirm('You have unsaved changes. Switch to different client anyway?')) {
        loadClient(id);
        setIsOpen(false);
      }
    } else {
      loadClient(id);
      setIsOpen(false);
    }
  };
  
  const displayName = currentClient?.company?.legalName || 
    (currentClient && !currentClient.id ? 'Unsaved Analysis' : 'No Client');
  
  const eligibleCount = currentClient?.summary?.eligible || 0;
  const totalCount = currentClient?.summary?.totalEmployees || currentClient?.employees?.length || 0;
  
  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          color: C.text,
          cursor: 'pointer',
          minWidth: 200,
        }}
      >
        <Icon name="briefcase-outline" style={{ fontSize: 16, color: C.accent }} />
        <div style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        {totalCount > 0 && (
          <span style={{ 
            fontSize: 10, 
            color: C.textLight, 
            background: C.bg, 
            padding: '2px 6px', 
            borderRadius: 4,
            fontWeight: 700,
          }}>
            {eligibleCount}/{totalCount}
          </span>
        )}
        <Icon name={isOpen ? "chevron-up" : "chevron-down"} style={{ fontSize: 14, color: C.textMid }} />
      </button>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 8,
          width: 400,
          maxHeight: 500,
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 9999,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ position: 'relative' }}>
              <Icon name="search-outline" style={{ 
                position: 'absolute', 
                left: 10, 
                top: '50%', 
                transform: 'translateY(-50%)', 
                fontSize: 14, 
                color: C.textLight 
              }} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search employers..."
                style={{
                  ...S.input,
                  fontSize: 12,
                  paddingLeft: 32,
                }}
              />
            </div>
          </div>
          
          {/* Scrollable Content */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {/* Unsaved Analysis */}
            {currentClient && !currentClient.id && (
              <>
                <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Current
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  background: hasUnsavedChanges ? C.orangeLight : 'transparent',
                  borderBottom: `1px solid ${C.border}`,
                }}>
                  <button
                    onClick={() => setIsOpen(false)}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <Icon name="document-text-outline" style={{ fontSize: 16, color: C.orange }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                        Unsaved Analysis
                      </div>
                      {totalCount > 0 && (
                        <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
                          {eligibleCount}/{totalCount} eligible
                        </div>
                      )}
                    </div>
                    {hasUnsavedChanges && (
                      <span style={{ fontSize: 9, color: C.orange, fontWeight: 700 }}>UNSAVED</span>
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (window.confirm('Clear unsaved analysis and start fresh?')) {
                        clearUnsaved();
                        setIsOpen(false);
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: C.red,
                      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                      touchAction: 'manipulation',
                    }}
                    title="Clear unsaved analysis"
                  >
                    <Icon name="trash-outline" style={{ fontSize: 16 }} />
                  </button>
                </div>
              </>
            )}
            
            {/* Recent Clients */}
            {recentClients.length > 0 && (
              <>
                <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 8 }}>
                  Recent
                </div>
                {recentClients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleLoadClient(client.id)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: currentClient?.id === client.id ? C.accentLight : 'transparent',
                      border: 'none',
                      borderBottom: `1px solid ${C.border}`,
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    {getStageIcon(client.crm.stage)}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                        {client.company.legalName || 'Unnamed Client'}
                      </div>
                      <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
                        {client.summary?.eligible || 0}/{client.summary?.totalEmployees || 0} eligible · Updated {getRelativeTime(client.updatedAt)}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
            
            {/* All Clients (if searching) */}
            {searchTerm && filteredClients.length > 0 && (
              <>
                <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 8 }}>
                  Search Results
                </div>
                {filteredClients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleLoadClient(client.id)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: currentClient?.id === client.id ? C.accentLight : 'transparent',
                      border: 'none',
                      borderBottom: `1px solid ${C.border}`,
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    {getStageIcon(client.crm.stage)}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                        {client.company.legalName || 'Unnamed Client'}
                      </div>
                      <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
                        {client.summary?.eligible || 0}/{client.summary?.totalEmployees || 0} · {PIPELINE_STAGES[client.crm.stage]?.label}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
            
            {searchTerm && filteredClients.length === 0 && (
              <div style={{ padding: '20px 14px', textAlign: 'center', color: C.textLight, fontSize: 12 }}>
                No clients found
              </div>
            )}
          </div>
          
          {/* Footer Actions */}
          <div style={{ borderTop: `1px solid ${C.border}`, padding: '8px' }}>
            {/* Deselect/Clear Client button - only show if a client is currently selected */}
            {currentClient && currentClient.id && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (window.confirm('Deselect current client and start fresh?')) {
                    clearUnsaved();
                    setIsOpen(false);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 14px',
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textMid,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginBottom: 6,
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                  touchAction: 'manipulation',
                }}
              >
                <Icon name="close-circle-outline" style={{ fontSize: 16 }} />
                Clear Selection
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNewClient();
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: C.greenLight,
                border: `1px solid ${C.green}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
                color: C.green,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                touchAction: 'manipulation',
              }}
            >
              <Icon name="add-circle-outline" style={{ fontSize: 16 }} />
              New Client
            </button>
          </div>
        </div>
      )}
      
      {/* Save Employer Dialog */}
      <SaveEmployerDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={() => {
          setShowSaveDialog(false);
        }}
      />
    </div>
  );
}
